import { useMemo, useRef } from 'react';
import { type QueryKey, useQueries, useQuery } from '@tanstack/react-query';

import type { LiceSource } from '@/hooks/useLiceSource';
import { useLiceSource } from '@/hooks/useLiceSource';
import type { SwaggerResponse } from '@/services/config';
import type { LiceParityDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import {
    fetchWeeklyLiceReport,
    type WeeklyLiceReportQuery,
    type WeeklyLiceReportResponseDto,
} from './liceReportClient';
import { mapAllWeeklyReportsToDomain } from './mappers';
import type { AllWeeklyReportResult } from './types';

/**
 * One ISO week of the lice report.
 *
 * `source` comes from `useLiceSource()`, the same way `useGetAllWeeklyLiceReport` sends it, and
 * the first request does not wait for the flag: until PostHog resolves, the source is `sql`.
 * When the flag then settles on `core`, `placeholderData` keeps the `sql` payload on screen
 * instead of dropping back to the skeleton. A different week or location does not reuse it.
 *
 * Expected generated hook, once `updateApi` has run: `useClientController_LiceWeeklyReport`.
 */
export const WEEKLY_LICE_REPORT_KEY = '/api/v2/client/internal/{clientId}/lice/weeklyReport';

interface WeeklyReportKeyParams {
    isoWeek: number;
    isoYear: number;
    locationId: number | null;
    source: string;
}

export interface UseGetWeeklyLiceReportResult {
    data: AllWeeklyReportResult | null;
    source: LiceSource | undefined;
    parity: LiceParityDto | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isFetched: boolean;
    error: unknown;
    refetch: () => unknown;
}

export interface SelectedLiceReportWeek {
    isoWeek: number;
    isoYear: number;
}

export interface UseGetSelectedWeeklyLiceReportsResult {
    data: AllWeeklyReportResult[];
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => void;
}

function isWeeklyReportKeyParams(value: unknown): value is WeeklyReportKeyParams {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>;
    return (
        typeof record.isoWeek === 'number' &&
        typeof record.isoYear === 'number' &&
        (typeof record.locationId === 'number' || record.locationId === null) &&
        typeof record.source === 'string'
    );
}

/**
 * Keep the previous response only when the next request is the same week and location and
 * the source is the only thing that changed.
 */
export function previousWeeklyReportWhenSourceChanges<T>(
    queryKey: QueryKey
): (previous: T | undefined, previousQuery?: { queryKey: QueryKey }) => T | undefined {
    return (previous, previousQuery) => {
        if (previous === undefined || previousQuery === undefined) {
            return undefined;
        }
        if (
            previousQuery.queryKey[0] !== queryKey[0] ||
            previousQuery.queryKey[1] !== queryKey[1]
        ) {
            return undefined;
        }
        const previousParams = previousQuery.queryKey[2];
        const nextParams = queryKey[2];
        if (!isWeeklyReportKeyParams(previousParams) || !isWeeklyReportKeyParams(nextParams)) {
            return undefined;
        }
        const sameReport =
            previousParams.isoWeek === nextParams.isoWeek &&
            previousParams.isoYear === nextParams.isoYear &&
            previousParams.locationId === nextParams.locationId;
        return sameReport && previousParams.source !== nextParams.source ? previous : undefined;
    };
}

/**
 * `useQueries` calls `placeholderData` with no previous query, so a source change cannot read
 * the response that is already on screen. Remember it by client, location, and week instead.
 * The source is deliberately not part of the key: that is the only change this should survive.
 */
function selectedWeekMemoryKey(
    clientId: number,
    isoWeek: number,
    isoYear: number,
    locationId: number | undefined
): string {
    return `${clientId}:${locationId ?? 'all'}:${isoYear}:${isoWeek}`;
}

function weeklyReportQueryKey(
    clientId: number,
    query: WeeklyLiceReportQuery
): readonly [string, number, WeeklyReportKeyParams] {
    return [
        WEEKLY_LICE_REPORT_KEY,
        clientId,
        {
            isoWeek: query.isoWeek,
            isoYear: query.isoYear,
            locationId: query.locationId ?? null,
            source: query.source,
        },
    ];
}

function weeklyReportQueryOptions(
    clientId: number,
    query: WeeklyLiceReportQuery,
    options?: DomainQueryOptions<WeeklyLiceReportResponseDto>
) {
    const queryKey = weeklyReportQueryKey(clientId, query);
    return {
        ...options,
        queryKey,
        queryFn: () => fetchWeeklyLiceReport(clientId, query),
        placeholderData:
            previousWeeklyReportWhenSourceChanges<SwaggerResponse<WeeklyLiceReportResponseDto>>(
                queryKey
            ),
    };
}

export function useGetWeeklyLiceReport(
    clientId: number,
    isoWeek: number,
    isoYear: number,
    locationId?: number,
    options?: DomainQueryOptions<WeeklyLiceReportResponseDto>
): UseGetWeeklyLiceReportResult {
    const source = useLiceSource();
    const query = useQuery(
        weeklyReportQueryOptions(clientId, { isoWeek, isoYear, locationId, source }, options)
    );
    const response = query.data?.data;
    const data = useMemo(
        () => (response?.report ? mapAllWeeklyReportsToDomain([response.report])[0] : null),
        [response?.report]
    );

    return {
        data,
        source: response?.source,
        parity: response?.parity,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
        refetch: query.refetch,
    };
}

/**
 * The weeks the reader has selected. Each one is its own query, so adding a week does not
 * reload the weeks already on screen.
 */
export function useGetSelectedWeeklyLiceReports(
    clientId: number,
    weeks: readonly SelectedLiceReportWeek[],
    locationId?: number
): UseGetSelectedWeeklyLiceReportsResult {
    const source = useLiceSource();
    const rememberedReports = useRef(
        new Map<string, SwaggerResponse<WeeklyLiceReportResponseDto>>()
    );
    const queries = useMemo(
        () =>
            weeks.map((week) => {
                const memoryKey = selectedWeekMemoryKey(
                    clientId,
                    week.isoWeek,
                    week.isoYear,
                    locationId
                );
                const query = { isoWeek: week.isoWeek, isoYear: week.isoYear, locationId, source };
                return {
                    ...weeklyReportQueryOptions(clientId, query),
                    queryFn: () =>
                        fetchWeeklyLiceReport(clientId, query).then((response) => {
                            rememberedReports.current.set(memoryKey, response);
                            return response;
                        }),
                    placeholderData: () => rememberedReports.current.get(memoryKey),
                };
            }),
        [clientId, weeks, locationId, source]
    );
    const results = useQueries({ queries });

    const data = useMemo(
        () =>
            results.flatMap((result) => {
                const report = result.data?.data.report;
                return report ? mapAllWeeklyReportsToDomain([report]) : [];
            }),
        [results]
    );

    return {
        data,
        isLoading: results.some((result) => result.isPending),
        isFetching: results.some((result) => result.isFetching),
        error: results.find((result) => result.error)?.error ?? null,
        refetch: () => {
            for (const result of results) {
                if (result.error) {
                    void result.refetch();
                }
            }
        },
    };
}
